import { useEffect, useState } from "react";
import { Button, Card, Icon, Loading } from "@/components/atoms";
import { FlightTrackingModal } from "@/components/organisms/AddOns";
import { useOperator } from "@/hooks";
import { useGetPremiumAddOnsQuery } from "@/redux/apis/addOn/addOnApi";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { addOnItemSelector } from "@/redux/slices/addOn/selectors";
import { setAction } from "@/redux/slices/booking/bookingSlice";
import { BookingDetails } from "@/redux/slices/booking/types";
import { clsx } from "@/utils";
import { BookingFlightStatusCode } from "../../../common/BookingFlightStatusCode/BookingFlightStatusCode";
import { DetailField } from "../../../common/DetailField";

interface FlightSectionProps {
  booking: BookingDetails;
  onModalStateChange?: (isOpen: boolean) => void;
}

export const FlightSection = ({ booking, onModalStateChange }: FlightSectionProps) => {
  const dispatch = useAppDispatch();
  const { roles } = useOperator();
  const { isFetching } = useGetPremiumAddOnsQuery();
  const flightTracking = useAppSelector(addOnItemSelector("flight-tracking"));
  const isEnabled = flightTracking?.enabled;
  const isAdmin = roles.includes("Fa_Access") || roles.includes("Owner") || roles.includes("Admin");
  const [step, setStep] = useState<"add-on" | undefined>(undefined);
  const flight = booking.stops.find((stop) => stop.flight)?.flight;

  useEffect(() => {
    onModalStateChange?.(Boolean(step));
  }, [step, onModalStateChange]);

  const handleClick = () => {
    if (isEnabled) dispatch(setAction("flight-details"));
  };

  if (!flight) return null;

  // `scheduled` is not "we know nothing" — it is where every flight booking sits until roughly three
  // days out, carrying a published schedule the whole time. Only not_found and unknown carry nothing.
  const hasData = flight.status !== "not_found" && flight.status !== "unknown";
  const statusClasses = {
    "is-enabled": isEnabled,
    "has-data": hasData || !isEnabled,
    "is-cancelled": flight.status === "cancelled",
  };

  return (
    <>
      <div className="group/flight-unlock relative">
        {isFetching && <Loading className="z-30 rounded-lg" />}
        <Card className={clsx("relative p-2.5", statusClasses)}>
          <div className="absolute right-2 top-2">
            {isEnabled && <BookingFlightStatusCode status={flight.status} delay={flight.delay} type="default" />}
          </div>

          <DetailField
            leading={<Icon name="Airplane" size="lg" className="text-neutral-dark-gray" />}
            label="Flight Details"
            value={`${flight.airline ? flight.airline.name : ""} (${flight.number})${
              flight.arrival.terminal ? ` - Terminal ${flight.arrival.terminal}` : ""
            }`}
          />

          <div className="mt-3 flex justify-stretch">
            {isEnabled ? (
              <Button
                variant="tertiary-light"
                size="sm"
                endIcon="ArrowRight"
                onClick={handleClick}
                className="text-info hover:bg-transparent hover:text-info-dark"
              >
                View Flight Details
              </Button>
            ) : (
              <Button
                endIcon={<Icon name="Diamonds" variant="Bold" size="md" className="text-warning" />}
                variant="primary"
                onClick={() => setStep("add-on")}
                className="w-full"
              >
                Get Flight Tracking
              </Button>
            )}
          </div>
        </Card>
      </div>
      <FlightTrackingModal open={step === "add-on"} onClose={() => setStep(undefined)} isAdmin={isAdmin} />
    </>
  );
};
